"use client"

import * as React from "react"
import { Check, X, ChevronsUpDown } from "lucide-react"
import type { UseFormReturn } from "react-hook-form"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { FormItem, FormLabel, FormDescription, FormMessage, FormField } from "@/components/ui/form"
import type { FormElement } from "./types"

interface FormMultiSelectProps {
  element: FormElement
  form: UseFormReturn<any>
}

export function FormMultiSelect({ element, form }: FormMultiSelectProps) {
  const [open, setOpen] = React.useState(false)

  // Build options from children
  const options = React.useMemo(() => {
    const opts: Array<{ value: string; label: string; group?: string }> = []

    element.children?.forEach((child) => {
      if (child.type === "option") {
        opts.push({
          value: child.value || "",
          label: child.ui?.text || child.value || "",
        })
      } else if (child.type === "optgroup") {
        const groupLabel = child.attributes?.label
        child.children?.forEach((option) => {
          if (option.type === "option") {
            opts.push({
              value: option.value || "",
              label: option.ui?.text || option.value || "",
              group: groupLabel,
            })
          }
        })
      }
    })

    return opts
  }, [element.children])

  const { name, validation, ui } = element

  return (
    <FormField
      control={form.control}
      name={name!}
      rules={{
        required: validation?.required ? `${ui?.label || name} is required` : false,
      }}
      render={({ field }) => {
        const selectedValues = Array.isArray(field.value) ? field.value : []

        const handleSelect = (value: string) => {
          const newValues = selectedValues.includes(value)
            ? selectedValues.filter((v) => v !== value)
            : [...selectedValues, value]
          field.onChange(newValues)
        }

        const handleRemove = (value: string) => {
          field.onChange(selectedValues.filter((v) => v !== value))
        }

        return (
          <FormItem>
            {ui?.label && <FormLabel>{ui.label}</FormLabel>}
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="w-full justify-between min-h-10 h-auto bg-transparent"
                >
                  <div className="flex flex-wrap gap-1 flex-1">
                    {selectedValues.length > 0 ? (
                      selectedValues.map((value) => {
                        const option = options.find((o) => o.value === value)
                        return (
                          <Badge key={value} variant="secondary" className="mr-1">
                            {option?.label || value}
                            <button
                              type="button"
                              className="ml-1 rounded-full outline-hidden ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                              onMouseDown={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                              }}
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                handleRemove(value)
                              }}
                            >
                              <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                            </button>
                          </Badge>
                        )
                      })
                    ) : (
                      <span className="text-muted-foreground">{ui?.placeholder || "Select options..."}</span>
                    )}
                  </div>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput placeholder={`Search ${ui?.label?.toLowerCase() || "options"}...`} />
                  <CommandList>
                    <CommandEmpty>No option found.</CommandEmpty>
                    {Array.from(new Set(options.map((o) => o.group).filter(Boolean))).length > 0 ? (
                      Array.from(new Set(options.map((o) => o.group))).map((group) => (
                        <CommandGroup key={group || "default"} heading={group}>
                          {options
                            .filter((opt) => opt.group === group)
                            .map((option) => (
                              <CommandItem
                                key={option.value}
                                value={option.value}
                                onSelect={() => handleSelect(option.value)}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedValues.includes(option.value) ? "opacity-100" : "opacity-0",
                                  )}
                                />
                                {option.label}
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      ))
                    ) : (
                      <CommandGroup>
                        {options.map((option) => (
                          <CommandItem
                            key={option.value}
                            value={option.value}
                            onSelect={() => handleSelect(option.value)}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedValues.includes(option.value) ? "opacity-100" : "opacity-0",
                              )}
                            />
                            {option.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {ui?.hint && <FormDescription>{ui.hint}</FormDescription>}
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}

